import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, filter, map, of } from 'rxjs';
import { environment } from '@env/environment';
import { FeatureDto, FeatureViewService } from '@vpfa/rest-api/admin';
import { FeatureFlagKey } from '../types/feature-flag-key';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  #featureFlagsConfig$ = new BehaviorSubject<FeatureDto[] | null>(null);
  #flagsStatus$ = new BehaviorSubject({ loaded: false, isLoading: false });

  constructor(
    @Inject(ApplicationRef) private readonly appRef: ApplicationRef,
    private featureFlagsView: FeatureViewService,
  ) {
    this.allowModifyingFlagsFromDevTools();
    this.initDebugUtils();
  }

  public getStatus() {
    return this.#flagsStatus$.asObservable();
  }

  public loadFlags(): Observable<FeatureDto[]> {
    return this.featureFlagsView.getAllFlagsWithContext();
  }

  public init(): Observable<FeatureDto[]> {
    if (!!this.#featureFlagsConfig$.value) {
      return of(this.#featureFlagsConfig$.value);
    }

    this.#flagsStatus$.next({ loaded: false, isLoading: true });

    return this.loadFlags().pipe(
      map(flagsStatus => {
        this.#featureFlagsConfig$.next(flagsStatus);

        this.#flagsStatus$.next({ loaded: true, isLoading: false });

        return flagsStatus;
      }),
    );
  }

  public clear() {
    this.#featureFlagsConfig$.next(null);
  }

  public isFeatureEnabled(flagName: FeatureFlagKey): boolean {
    if (this.#featureFlagsConfig$.value === null) {
      console.warn('Feature Flags not loaded yet');

      return false;
    }

    return (
      this.#featureFlagsConfig$.value?.find(x => x.flagkey.toLowerCase() === flagName.toLowerCase())?.status ?? false
    );
  }

  public isFeatureEnabled$(flagName: FeatureFlagKey): Observable<boolean> {
    return combineLatest([this.#flagsStatus$, this.#featureFlagsConfig$]).pipe(
      // wait for flags to be loaded
      filter(([flagLoadingStatus]) => flagLoadingStatus.loaded === true && flagLoadingStatus.isLoading === false),
      map(([, config]) => {
        return this.findFeatureFlag(config ?? [], flagName)?.status ?? false;
      }),
    );
  }

  /**
   * Update flag on a runtime. May not work with all cases where `isFeatureEnabled`
   * is used instead of observable `isFeatureEnabled$`.
   * @param flagName
   * @param value
   */
  public updateFlag(flagName: FeatureFlagKey, value: boolean): void {
    const flag = this.findFeatureFlag(this.#featureFlagsConfig$.value ?? [], flagName);

    if (!flag) {
      this.#featureFlagsConfig$.next([
        ...(this.#featureFlagsConfig$.value ?? []),
        { flagkey: flagName, status: value },
      ]);
      console.warn(`Added flag ${flagName} with status: ${String(value)}`);
      return;
    }

    flag.status = value;

    this.#featureFlagsConfig$.next([...(this.#featureFlagsConfig$.value ?? [])]);

    // force update of a view
    this.appRef.tick();
  }

  private findFeatureFlag(input: FeatureDto[], flagName: FeatureFlagKey): FeatureDto | undefined {
    const found = input.find(x => x.flagkey.toLowerCase() === flagName.toLowerCase());

    if (!found) {
      console.error(`Feature Flag: "${flagName}" not found`);
    }

    return found;
  }

  /**
   * Allows to update flag from Browser's DevTools. Example:
   *
   * `window.featureFlags.service.updateFlag('test', true); // window can be omitted`
   *
   * Will work only for FE flags.
   *
   * May not work with all features.
   */
  private allowModifyingFlagsFromDevTools(): void {
    if (!environment.production) {
      if (!(window as any).featureFlags) {
        (window as any).featureFlags = {};
      }

      (window as any).featureFlags.service = this;
    }
  }

  private initDebugUtils() {
    if (!(window as any).featureFlags) {
      (window as any).featureFlags = {};
    }

    (window as any).featureFlags.list = (flagNameFilter: string | null = null) => {
      const flags = this.#featureFlagsConfig$.value?.filter(x => {
        if (!flagNameFilter) {
          // return all flags if filter not set
          return true;
        }

        return x.flagkey.toLowerCase().includes(flagNameFilter.toLowerCase());
      });

      console.table(flags);
      return flags;
    };
  }
}
